import React, { useEffect, useRef, useState } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import "../FundsDeposited/fundsDeposited.scss";
import SearchBar from "../../common/SearchBar";
import { useDispatch, useSelector } from "react-redux";
import { getFundsDeposited, getTransactions } from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import { separateDateTimeAndConvertToLocale } from "../../../utils/utilFunctions";
import debounce from "lodash.debounce"; // Ensure lodash.debounce is installed
import PrimaryButton from "../../common/PrimaryButton";
import Box from "../../common/Dashboard/Box";
import ReleaseIcon from "../../../Assets/Icons/releaseIcon.svg";
import FundsReceivedIcon from "../../../Assets/Icons/fundsReceivedIcon.svg";
import WithdrawalsDataTable from "../Withdrawals";
import FundsDepositedTable from "../FundsDeposited";
import WithdrawModal from "./WithdrawModal";

const TransactionsPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const [allFunds, setAllFunds] = useState([]);
  const [cards, setCards] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [withdrawFunds, setWithdrawFunds] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filter, setFilter] = useState("Year");

  const {totalEarned, availableFunds, pendingFunds} = cards;
  const handleSelect = (index) => {
    setSelectedIndex(index);
  };

  const lastFilterRef = useRef(null);

  const getTransactionsData = async () => {
    try {
      const transactionsPromise = await dispatch(
        getTransactions({ filter: filter?.toLowerCase(), silent: true })
      )?.unwrap();
      if (transactionsPromise?.success === true) {
        setCards(transactionsPromise?.cards);
      } else {
        setCards({});
      }
    } catch (e) {
      // Gracefully handle empty/new accounts or network errors
      setCards({});
    }
  };

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query) => {
        // getFundsData(query);
      }, 300), // 300ms debounce time
    []
  );

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    debouncedSearch(value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // getFundsData(searchQuery);
  };

  useEffect(() => {
    // Only fetch once per unique filter value (prevents StrictMode double calls)
    if (lastFilterRef.current === filter) return;
    lastFilterRef.current = filter;
    getTransactionsData();
  }, [filter]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const items = [
    {
      title: "Transactions",
    },
    {
      title: "All Transactions",
    },
  ];

  const columns = [
    {
      name: "No.",
    },
    {
      name: "Date",
    },
    {
      name: "User Name",
    },
    {
      name: "Email",
    },
    {
      name: "Balance",
    },
  ];

  const handleWithdraw = () => {
    setWithdrawFunds(true);
  };

  const fundraiserBoxes = [
    {
      text: "Total Earned",
      number: `$${totalEarned ? (totalEarned/100).toFixed(2) : 0}`,
      icon: null,
      filter: filter
    },
    {
      text: "Available Funds",
      number: `$${availableFunds ? (availableFunds/100).toFixed(2) : 0}`,
      icon: FundsReceivedIcon,
    },
    {
      text: "Pending Funds",
      number: `$${pendingFunds ? (pendingFunds/100).toFixed(2) : 0}`,
      icon: ReleaseIcon,
    },
  ];

  const data = [
    {
      text: "Credit",
    },
    {
      text: "Debit",
    },
  ];

  return (
    <>
      {loading === true && <Loader />}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center col-span-12">
        <BreadCrumsHeader
          heading="Transactions"
          items={items}
          form={true}
        />
        <PrimaryButton
          className={
            "self-start sm:self-auto w-[60%] sm:w-[50%] md:w-[30%] lg:w-[20%] h-[45px] rounded-[5px] text-[16px] font-[500] font-montserrat mt-3 sm:mt-0"
          }
          label="Withdraw Funds"
          onClick={() => handleWithdraw()}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 w-full gap-3 my-[2%]">
        {fundraiserBoxes?.map((b) => (
          <Box
            text={b?.text}
            number={b?.number}
            icon={b?.icon}
            mainClassName="box p-3 h-[113px]"
            textClassName="text-[14px] font-poppins font-[600] text-[white]"
            numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
            reverse={true}
            filter={b?.filter}
            setFilter={b?.filter && setFilter}
          />
        ))}
      </div>
      <div className="flex flex-col sm:flex-row justify-start items-stretch sm:items-center gap-3 sm:gap-4">
        <SearchBar
          parentDivClass="w-full sm:w-[50%] lg:w-[33%] relative"
          divClassName="relative"
          inputClassName="searchBar md:!h-[46px] p-3 w-full text-[14px] font-poppins font-[400] outline-none"
          iconClassName="absolute right-[4%] top-1/2 -translate-y-1/2"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
        />
        <div className="w-full sm:w-[50%] lg:w-[67%] max-w-md flex justify-center items-center">
          <div className="w-full h-[40px] md:h-[46px] grid grid-cols-2 rounded-[6px] border border-[#00ADEF] p-1">
            {data.map((item, index) => (
              <div
                key={index}
                className={`rounded-[6px] flex justify-center items-center cursor-pointer transition-colors duration-200 ${
                  selectedIndex === index ? "bg-[#00ADEF]" : "bg-white"
                }`}
                onClick={() => handleSelect(index)}
              >
                <p
                  className={`font-sans text-center font-medium text-base ${
                    selectedIndex === index ? "text-white" : "text-[#8C8C8C]"
                  }`}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedIndex == 1 ? <FundsDepositedTable /> : <WithdrawalsDataTable />}

      <WithdrawModal
        isOpen={withdrawFunds}
        onClose={() => setWithdrawFunds(false)}
        availableFundsCents={Number(availableFunds) || 0}
        onWithdrawSuccess={getTransactionsData}
      />
    </>
  );
};

export default TransactionsPage;
